import { useSearchParams } from "react-router-dom";
import { BarChart3, Library, Receipt, ListOrdered, SlidersHorizontal, Utensils } from "lucide-react";
import ReportsPanelV2 from "@/pages/ReportsPanelV2";
import ReportPackBrowser from "@/components/reports/ReportPackBrowser";
import CustomReportsView from "@/components/reports/CustomReportsView";
import { PageShell } from "@/components/PageShell";
import { usePOS } from "@/context/POSContext";
import { hasAnyReport, knowsModules } from "@/lib/access";
import { REPORT_CATALOGUE } from "@/components/reports/reportCatalogue";

// The three reports staff ask for by name come first (owner, 2026-10-13), each a
// dense table on the same closed-bill scope, so they reconcile with one another:
// Sales report (day totals, discounts, tax, tenders, cancellations), Item sales
// (every item sold, quantity and value) and Sale summary (one row per bill).
const tabs = [
  ["sales-report", "Sales report", ListOrdered, "orders-sales-report"],
  ["item-sales", "Item sales", Utensils, "items-category-items"],
  ["sale-summary", "Sale summary", Receipt, "orders-master"],
  ["sales", "Charts", BarChart3],
  ["library", "Report library", Library],
  // Built by the owner in Settings → Reports (2026-10-24).
  ["custom", "Custom reports", SlidersHorizontal],
];

// Charts reads these legacy summaries all at once (hooks/useReports).
const CHART_REPORTS = ["report.sales", "report.eod", "report.taxes"];

export default function EnterpriseReportsPage() {
  const { user, permissions } = usePOS();
  // Each report is a per-person switch (2026-10-20): a tab shows only when the
  // person holds its report(s). The server refuses the rest regardless.
  const visibleTabs = tabs.filter(([id, , , slug]) => {
    if (!knowsModules(user, permissions)) return true;
    if (slug) return permissions.includes(`report.${slug}`);
    if (id === "sales") return CHART_REPORTS.every(key => permissions.includes(key));
    if (id === "custom") return permissions.includes("reports.view");
    return REPORT_CATALOGUE.some(entry => permissions.includes(`report.${entry.slug}`));
  });
  // The tab rides in the address, so Ctrl+S / Ctrl+I (useReportShortcuts) open
  // the right one, even from this page.
  const [searchParams, setSearchParams] = useSearchParams();
  const chosen = searchParams.get("tab") || "sales-report";
  const setTab = id => setSearchParams({ tab: id });
  const tab = visibleTabs.some(([id]) => id === chosen) ? chosen : visibleTabs[0]?.[0];
  const fixed = tabs.find(([id]) => id === tab)?.[3];

  return (
    /*
     * The hero here was three lines that said "Reports" three ways — REPORTING &
     * AUDIT, then "Business intelligence", then "Operational, tax, inventory and
     * security reporting" — under a topbar already reading Reports. The tab strip
     * below is what actually tells the owner where they are.
     */
    <PageShell testId="enterprise-reports-page">
      <div className="flex gap-snug overflow-x-auto scrollbar-hide border-b shrink-0 scroll-contain-x" data-testid="report-module-tabs">
        {visibleTabs.map(([id, label, Icon]) => {
          const isActive = tab === id;
          return (
            <button
              key={id}
              className={`flex items-center gap-snug px-base py-snug -mb-px text-body font-medium transition-colors border-b-2 whitespace-nowrap ${isActive ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              onClick={() => setTab(id)}
              data-testid={`reports-tab-${id}-button`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          )
        })}
      </div>

      {/* One instance across the three named tabs, so the chosen dates carry over. */}
      {fixed && <ReportPackBrowser fixedSlug={fixed} />}
      {tab === "sales" && <ReportsPanelV2 />}
      {tab === "library" && <ReportPackBrowser />}
      {tab === "custom" && <CustomReportsView />}
      {!visibleTabs.length && (
        <p className="p-base text-meta text-muted-foreground" data-testid="reports-none-enabled">
          {hasAnyReport(permissions)
            ? "Your reports are not on this page yet. An admin can change your reports in Staff."
            : "No reports are enabled for you. An admin can allow them in Staff."}
        </p>
      )}
    </PageShell>
  );
}
